import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Brain, Zap, MessageSquare, BrainCircuit, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AISubmissionInsightCardProps {
  analysis?: {
    sentiment: 'positive' | 'neutral' | 'negative';
    intent: string;
    summary: string;
    priority: 'low' | 'medium' | 'high';
  };
  customerName: string;
  formName: string;
  isProcessing?: boolean;
}

export const AISubmissionInsightCard = ({ 
  analysis, 
  customerName, 
  formName,
  isProcessing = false 
}: AISubmissionInsightCardProps) => {
  
  if (isProcessing || !analysis) {
    return (
      <Card className="relative overflow-hidden border-amber-500/10 bg-amber-500/5 animate-pulse">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/5 to-transparent skew-x-[-20deg] animate-shimmer" />
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="space-y-4 flex-1">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 text-amber-500 animate-spin" />
                <span className="text-sm font-semibold text-amber-600 dark:text-amber-500">AI is analyzing this response...</span>
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-amber-500/10 rounded w-1/3" />
                <div className="h-4 bg-amber-500/10 rounded w-2/3" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getSentimentStyles = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return "bg-success/10 text-success border-success/20";
      case 'negative': return "bg-destructive/10 text-destructive border-destructive/20";
      default: return "bg-muted text-muted-foreground border-border";
    }
  };

  const getPriorityStyles = (priority: string) => {
    switch (priority) {
      case 'high': return "text-amber-600 dark:text-amber-500 font-bold";
      case 'medium': return "text-blue-600 dark:text-blue-400 font-semibold";
      default: return "text-muted-foreground";
    }
  };

  return (
    <Card className="group relative overflow-hidden card-elegant border-amber-500/20 hover:border-amber-500/40 hover:shadow-glow-amber transition-all duration-500">
      <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
        <BrainCircuit className="h-12 w-12 text-amber-500" />
      </div>
      
      <CardContent className="p-6">
        <div className="flex flex-col gap-4">
          {/* Header Info */}
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <h4 className="text-sm font-medium text-muted-foreground">Latest Response from <span className="text-foreground">{customerName}</span></h4>
              <p className="text-xs text-muted-foreground/70">via {formName}</p>
            </div>
            <div className="flex items-center gap-2">
               <Badge variant="outline" className={cn("text-[10px] uppercase tracking-wider font-bold", getSentimentStyles(analysis.sentiment))}>
                {analysis.sentiment}
              </Badge>
              <Badge variant="outline" className="bg-amber-500/5 text-amber-600 dark:text-amber-500 border-amber-500/20">
                <Zap className="w-3 h-3 mr-1" />
                {(analysis.priority || "").toUpperCase()}
              </Badge>
            </div>
          </div>

          {/* Main Insight */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              <span className="text-base font-bold text-foreground">{analysis.intent} Detected</span>
            </div>
            <p className="text-sm text-foreground leading-relaxed italic border-l-2 border-amber-500/20 pl-4 bg-amber-500/5 py-2 rounded-r-lg">
              "{analysis.summary}"
            </p>
          </div>

          {/* Quick Footer Stats */}
          <div className="flex items-center justify-between text-[10px] pt-4 border-t border-border/50">
             <div className="flex items-center gap-1 text-muted-foreground uppercase tracking-widest font-bold">
               <Brain className="w-3 h-3" />
               AI-Powered Intelligence
             </div>
             <span className={cn("uppercase tracking-widest font-bold", getPriorityStyles(analysis.priority))}>
               {analysis.priority} PRIORITY
             </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
